import React from 'react';
import {Link} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiTrendingUp,FiZap,FiTarget,FiCheck,FiArrowRight,FiCode,FiShoppingCart,FiSettings,FiDatabase,FiShield,FiSmartphone}=FiIcons;

function NoCodeDevelopment() {
  const services=[ 
    {icon: FiCode,title: 'Custom Business Applications',description: 'Tailored applications that solve your specific business challenges and workflows.',timeline: '2-6 weeks depending on complexity',benefits: [ 
      'Custom application design and development',
      'User-friendly interfaces optimized for your team',
      'Integration with your existing business tools',
      'Mobile-responsive design for any device',
      'Complete training and documentation' 
    ],perfectFor: [ 
      'Project management and tracking systems',
      'Customer relationship management (CRM)',
      'Inventory and asset management',
      'Employee onboarding and training platforms',
      'Custom reporting and analytics dashboards' 
    ]},
    {icon: FiShoppingCart,title: 'E-commerce & Marketplace Platforms',description: 'Complete online selling platforms with advanced features and integrations.',timeline: '3-8 weeks depending on features',benefits: [ 
      'Custom e-commerce platform development',
      'Payment processing and order management',
      'Inventory tracking and management',
      'Customer account and communication systems',
      'Analytics and reporting capabilities' 
    ],perfectFor: [ 
      'Online stores and marketplaces',
      'Service booking and scheduling platforms',
      'Membership and subscription sites',
      'Digital product delivery systems',
      'Multi-vendor marketplace platforms' 
    ]},
    {icon: FiSettings,title: 'Workflow Automation Platforms',description: 'Custom platforms that automate your business processes and improve efficiency.',timeline: '2-5 weeks depending on complexity',benefits: [ 
      'Process analysis and workflow design',
      'Custom automation platform development',
      'Integration with existing business tools',
      'Approval workflows and notifications',
      'Performance tracking and optimization' 
    ],perfectFor: [ 
      'Document approval and management systems',
      'Employee request and HR platforms',
      'Client onboarding and project management',
      'Quality control and compliance tracking',
      'Financial approval and expense management' 
    ]} 
  ];

  const caseStudies=[ 
    {title: 'Professional Services Firm',challenge: 'Manual project tracking across 50+ concurrent projects',solution: 'Custom project management platform with client portals',results: '60% reduction in administrative time,40% improvement in project delivery'},
    {title: 'E-commerce Business',challenge: 'Needed custom marketplace with unique vendor features',solution: 'Multi-vendor marketplace with custom commission structure',results: '300% increase in vendor applications,150% revenue growth'},
    {title: 'Manufacturing Company',challenge: 'Paper-based quality control and compliance tracking',solution: 'Digital quality management system with mobile access',results: '90% reduction in compliance errors,50% faster audits'} 
  ];

  const pricingTiers=[ 
    {name: 'Simple Business Applications',description: 'Basic workflow and data management applications',timeline: '2-3 weeks',features: ['User management','Data entry','Basic reporting'],investment: '$5,000-15,000'},
    {name: 'Advanced Business Platforms',description: 'Complex applications with integrations and advanced features',timeline: '4-6 weeks',features: ['API integrations','Advanced workflows','Custom reporting'],investment: '$15,000-35,000'},
    {name: 'Enterprise Solutions',description: 'Comprehensive platforms with multiple user types and complex logic',timeline: '6-10 weeks',features: ['Multi-tenant architecture','Advanced security','Custom integrations'],investment: '$35,000-75,000'} 
  ];

  return ( 
    <div> 
      {/* Hero Section */} 
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 py-20"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"> 
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6"> 
            Build Powerful Business Applications Without Traditional Development Costs 
          </h1> 
          <p className="text-xl text-blue-100 max-w-4xl mx-auto mb-10"> 
            Get custom software solutions built 10x faster and 70% cheaper than traditional development,with the flexibility to modify and scale as your business grows. 
          </p> 
          <Link 
            to="/book-consultation" 
            className="bg-white text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors" 
          > 
            Get Your Project Quote 
          </Link> 
        </div> 
      </section> 

      {/* Problem Section */} 
      <section className="py-16 bg-white"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"> 
              The Traditional Development Problem 
            </h2> 
            <p className="text-lg text-gray-600 max-w-4xl mx-auto mb-8"> 
              Most businesses need custom software but face significant barriers: 
            </p> 
          </div> 
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12"> 
            {[ 
              {title: 'High Costs',description: 'Traditional development costs $50,000-500,000+ for custom applications'},
              {title: 'Long Timelines',description: 'Projects take 6-18 months,missing critical business opportunities'},
              {title: 'Technical Complexity',description: 'Requires ongoing developer support for updates and changes'},
              {title: 'Inflexibility',description: 'Changes require expensive developer time and lengthy update cycles'} 
            ].map((problem,index)=> ( 
              <div key={index} className="bg-gray-50 p-6 rounded-xl"> 
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{problem.title}</h3> 
                <p className="text-gray-600">{problem.description}</p> 
              </div> 
            ))} 
          </div> 
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center"> 
            <p className="text-lg text-gray-700"> 
              <strong>Gartner predicts</strong> that by 2025,<span className="text-blue-600 font-semibold">70% of new applications</span> will be built using no-code/low-code platforms. 
            </p> 
          </div> 
        </div> 
      </section> 

      {/* Solution Section */} 
      <section className="py-16 bg-gray-50"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"> 
              No-Code Development That Delivers Enterprise Results 
            </h2> 
            <p className="text-xl text-gray-600 max-w-4xl mx-auto"> 
              Our no-code development approach delivers powerful applications with: 
            </p> 
          </div> 
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16"> 
            <div className="space-y-6"> 
              {[ 
                {title: 'Speed',description: 'Applications built in weeks,not months'},
                {title: 'Cost-Effectiveness',description: '70% less expensive than traditional development'},
                {title: 'Flexibility',description: 'Easy to modify and update as your business evolves'},
                {title: 'Scalability',description: 'Built to grow with your business needs'},
                {title: 'User-Friendly',description: 'Intuitive interfaces that your team will actually use'} 
              ].map((benefit,index)=> ( 
                <div key={index} className="flex items-start space-x-4"> 
                  <div className="flex-shrink-0"> 
                    <SafeIcon icon={FiCheck} className="h-6 w-6 text-green-500" /> 
                  </div> 
                  <div> 
                    <h3 className="text-lg font-semibold text-gray-900">{benefit.title}</h3> 
                    <p className="text-gray-600">{benefit.description}</p> 
                  </div> 
                </div> 
              ))} 
            </div> 
            <div className="bg-white p-8 rounded-xl shadow-lg"> 
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Process</h3> 
              <ol className="space-y-4"> 
                {[ 
                  'Discovery & Planning - Understand your requirements and design the optimal solution',
                  'Rapid Prototyping - Build and test core functionality quickly',
                  'Development & Testing - Create the full application with thorough testing',
                  'Training & Launch - Ensure your team can use and manage the application',
                  'Ongoing Support - Provide updates,improvements,and scaling support' 
                ].map((step,index)=> ( 
                  <li key={index} className="flex items-start space-x-3"> 
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium"> 
                      {index + 1} 
                    </span> 
                    <span className="text-gray-700">{step}</span> 
                  </li> 
                ))} 
              </ol> 
            </div> 
          </div> 
        </div> 
      </section> 

      {/* Services Section */} 
      <section className="py-16 bg-white"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"> 
              No-Code Development Services 
            </h2> 
          </div> 
          <div className="space-y-16"> 
            {services.map((service,index)=> ( 
              <div key={index} className="bg-gray-50 rounded-2xl p-8 lg:p-12"> 
                <div className="grid lg:grid-cols-2 gap-12"> 
                  <div> 
                    <div className="flex items-center space-x-4 mb-6"> 
                      <div className="p-3 bg-blue-100 rounded-xl"> 
                        <SafeIcon icon={service.icon} className="h-8 w-8 text-blue-600" /> 
                      </div> 
                      <div> 
                        <h3 className="text-2xl font-bold text-gray-900">{service.title}</h3> 
                        <p className="text-blue-600 font-semibold">Timeline: {service.timeline}</p> 
                      </div> 
                    </div> 
                    <p className="text-lg text-gray-600 mb-8">{service.description}</p> 
                    <div className="mb-8"> 
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">What You Get:</h4> 
                      <ul className="space-y-2"> 
                        {service.benefits.map((benefit,idx)=> ( 
                          <li key={idx} className="flex items-start space-x-3"> 
                            <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> 
                            <span className="text-gray-700">{benefit}</span> 
                          </li> 
                        ))} 
                      </ul> 
                    </div> 
                  </div> 
                  <div className="bg-white rounded-xl p-6 shadow-lg"> 
                    <h4 className="text-xl font-semibold text-gray-900 mb-4">Perfect For:</h4> 
                    <ul className="space-y-3"> 
                      {service.perfectFor.map((use,idx)=> ( 
                        <li key={idx} className="flex items-start space-x-3"> 
                          <SafeIcon icon={FiTarget} className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" /> 
                          <span className="text-gray-700">{use}</span> 
                        </li> 
                      ))} 
                    </ul> 
                  </div> 
                </div> 
              </div> 
            ))} 
          </div> 
        </div> 
      </section> 

      {/* Technology Section */} 
      <section className="py-16 bg-gray-50"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"> 
              Enterprise-Grade Technology,No-Code Simplicity 
            </h2> 
          </div> 
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12"> 
            {[ 
              {icon: FiDatabase,title: 'Database Management',description: 'Secure,scalable data storage and management'},
              {icon: FiShield,title: 'User Authentication',description: 'Secure login and permission systems'},
              {icon: FiZap,title: 'API Integrations',description: 'Connect with your existing business tools'},
              {icon: FiSmartphone,title: 'Mobile Optimization',description: 'Works perfectly on all devices'},
              {icon: FiSettings,title: 'Cloud Hosting',description: 'Reliable,secure,and scalable infrastructure'},
              {icon: FiShield,title: 'Backup & Security',description: 'Enterprise-level data protection'} 
            ].map((feature,index)=> ( 
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg text-center"> 
                <div className="text-blue-600 mb-4 flex justify-center"> 
                  <SafeIcon icon={feature.icon} className="h-10 w-10" /> 
                </div> 
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{feature.title}</h3> 
                <p className="text-gray-600">{feature.description}</p> 
              </div> 
            ))} 
          </div> 
          <div className="bg-white p-8 rounded-xl shadow-lg text-center"> 
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Built for Scale</h3> 
            <p className="text-lg text-gray-600 max-w-3xl mx-auto"> 
              Our applications are designed to handle growth,from startup to enterprise level usage. We connect with 200+ business applications for seamless integration. 
            </p> 
          </div> 
        </div> 
      </section> 

      {/* Results Section */} 
      <section className="py-16 bg-white"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"> 
              Real Applications,Real Results 
            </h2> 
          </div> 
          <div className="grid md:grid-cols-3 gap-8"> 
            {caseStudies.map((study,index)=> ( 
              <div key={index} className="bg-gray-50 p-8 rounded-xl"> 
                <h3 className="text-xl font-bold text-gray-900 mb-4">{study.title}</h3> 
                <div className="space-y-4"> 
                  <div> 
                    <h4 className="font-semibold text-gray-900 mb-2">Challenge:</h4> 
                    <p className="text-gray-600">{study.challenge}</p> 
                  </div> 
                  <div> 
                    <h4 className="font-semibold text-gray-900 mb-2">Solution:</h4> 
                    <p className="text-gray-600">{study.solution}</p> 
                  </div> 
                  <div> 
                    <h4 className="font-semibold text-gray-900 mb-2">Results:</h4> 
                    <p className="text-green-700 font-semibold">{study.results}</p> 
                  </div> 
                </div> 
              </div> 
            ))} 
          </div> 
        </div> 
      </section> 

      {/* Investment Section */} 
      <section className="py-16 bg-gray-50"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"> 
              Transparent Pricing,Exceptional Value 
            </h2> 
          </div> 
          <div className="grid md:grid-cols-3 gap-8 mb-12"> 
            {pricingTiers.map((tier,index)=> ( 
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg"> 
                <h3 className="text-xl font-bold text-gray-900 mb-4">{tier.name}</h3> 
                <p className="text-gray-600 mb-6">{tier.description}</p> 
                <div className="mb-6"> 
                  <div className="text-2xl font-bold text-blue-600 mb-2">{tier.investment}</div> 
                  <div className="text-sm text-gray-600">Timeline: {tier.timeline}</div> 
                </div> 
                <div className="mb-6"> 
                  <h4 className="font-semibold text-gray-900 mb-3">Features:</h4> 
                  <ul className="space-y-2"> 
                    {tier.features.map((feature,idx)=> ( 
                      <li key={idx} className="flex items-center space-x-2"> 
                        <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-500" /> 
                        <span className="text-gray-700">{feature}</span> 
                      </li> 
                    ))} 
                  </ul> 
                </div> 
              </div> 
            ))} 
          </div> 
          <div className="bg-white p-8 rounded-xl shadow-lg"> 
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Ongoing Support Options</h3> 
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"> 
              {[ 
                {service: 'Monthly updates and improvements',price: '$500-2,000/month'},
                {service: 'Feature additions and enhancements',price: '$1,000-5,000 per feature'},
                {service: 'Training and user support',price: '$150/hour'},
                {service: 'Rush projects (48-hour delivery)',price: '50% premium'} 
              ].map((support,idx)=> ( 
                <div key={idx} className="text-center"> 
                  <h4 className="font-semibold text-gray-900 mb-2">{support.service}</h4> 
                  <p className="text-blue-600 font-semibold">{support.price}</p> 
                </div> 
              ))} 
            </div> 
          </div> 
          <div className="text-center mt-12"> 
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 inline-block"> 
              <p className="text-lg text-gray-700"> 
                <strong className="text-green-700">Money-Back Guarantee:</strong> If we don't deliver a working application that meets your requirements,we'll refund your investment. 
              </p> 
            </div> 
          </div> 
        </div> 
      </section> 

      {/* CTA Section */} 
      <section className="py-16 bg-blue-900"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"> 
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6"> 
            Ready to Build Your Custom Business Application? 
          </h2> 
          <p className="text-xl text-blue-100 mb-8 max-w-4xl mx-auto"> 
            Schedule a complimentary Application Planning Session to discuss your requirements and get a detailed project proposal. 
          </p> 
          <div className="bg-white/10 rounded-xl p-6 max-w-2xl mx-auto mb-10"> 
            <h3 className="text-xl font-semibold text-white mb-4">What You'll Get:</h3> 
            <ul className="text-left space-y-2"> 
              {[ 
                'Requirements analysis and feasibility assessment',
                'Custom application mockups and workflow design',
                'Detailed project timeline and investment breakdown',
                'Technology recommendations and integration planning' 
              ].map((item,idx)=> ( 
                <li key={idx} className="flex items-start space-x-3"> 
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-300 mt-0.5 flex-shrink-0" /> 
                  <span className="text-blue-100">{item}</span> 
                </li> 
              ))} 
            </ul> 
          </div> 
          <Link 
            to="/book-consultation" 
            className="bg-white text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors" 
          > 
            Schedule Your Planning Session 
          </Link> 
        </div> 
      </section> 
    </div> 
  );
}

export default NoCodeDevelopment;